import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/unstable/httpapi';
import { OptionalUserAuthMiddleware, UserAuthMiddleware } from './middleware';
import { Duration, Schema } from 'effect';
import { RequestTimeout } from './request-timeout';
import {
  BadRequestError,
  NotFoundError,
  RateLimitedError,
  UnauthorizedError,
  InternalServerError,
} from './errors';
import {
  SynthesizePayload,
  SynthesizeResponse,
  TtsBatchTextResponseItem,
  TtsDetail,
} from './schemas/tts-dtos';
import { VoiceInput } from './schemas/voice';

export const DEMO_TEXT_MAX_LENGTH = 500;

const TTS_REQUEST_TIMEOUT = Duration.seconds(90);

const GetTtsUrlParams = Schema.Struct({
  voice: Schema.optional(VoiceInput),
});

// A single decoded TTS chunk. Shared by the chapter-TTS, single-TTS and demo
// endpoints — all return `{ tts: TtsDetail }`. `identifier` annotations keep
// these as named OpenAPI components (stable codegen names for the frozen
// Flutter/Expo clients) instead of anonymous inlined structs.
const TtsDetailResponse = Schema.Struct({ tts: TtsDetail }).annotate({
  identifier: 'TtsDetailResponse',
  description: 'Response envelope carrying a single TTS chunk.',
});

const BatchTextRequest = Schema.Struct({ text: Schema.String }).annotate({
  identifier: 'CreateTtsBatchTextRequest',
  description: 'Request body for batching text into streamable TTS chunks.',
});

const BatchTextResponse = Schema.Struct({
  batches: Schema.Array(TtsBatchTextResponseItem),
}).annotate({
  identifier: 'CreateTtsBatchTextResponse',
  description: 'A list of streamable text-to-speech batches.',
});

const DemoTextPayload = Schema.Struct({
  text: Schema.String,
  voice: Schema.optional(VoiceInput),
});

const getChapterTtsEndpoint = HttpApiEndpoint.get(
  'GetChapterTts',
  '/chapter/:chapterId/tts/:ttsId',
  {
    params: Schema.Struct({
      chapterId: Schema.String.annotate({
        description: 'Chapter ID',
      }),
      ttsId: Schema.String.annotate({
        description: 'TTS chunk ID',
      }),
    }),
    query: GetTtsUrlParams,
    success: TtsDetailResponse.pipe(HttpApiSchema.status(200)),
    error: [
      NotFoundError,
      UnauthorizedError,
      RateLimitedError,
      InternalServerError,
    ],
  },
)
  .middleware(OptionalUserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Chapter TTS Chunk (legacy)')
  .annotate(OpenApi.Deprecated, true)
  .annotate(
    OpenApi.Description,
    'Legacy chunk-id based TTS endpoint. New readers should synthesize from doc-pack page/char ranges through POST /v2/synthesize.',
  )
  .annotate(RequestTimeout, TTS_REQUEST_TIMEOUT);

const batchTextEndpoint = HttpApiEndpoint.post('BatchText', '/tts/batch-text', {
  payload: BatchTextRequest,
  success: BatchTextResponse.pipe(HttpApiSchema.status(200)),
  error: InternalServerError,
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Batch Text for TTS (legacy)')
  .annotate(OpenApi.Deprecated, true)
  .annotate(
    OpenApi.Description,
    'Legacy server-owned text chunking endpoint. New clients should use POST /v2/synthesize with reference ranges, or raw-text mode only for fallback/testing.',
  );

const getTtsEndpoint = HttpApiEndpoint.get('GetTts', '/tts/:ttsId', {
  params: Schema.Struct({
    ttsId: Schema.String.annotate({
      description: 'TTS chunk ID',
    }),
  }),
  query: GetTtsUrlParams,
  success: TtsDetailResponse.pipe(HttpApiSchema.status(200)),
  error: [NotFoundError, RateLimitedError, InternalServerError],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get TTS Chunk (legacy)')
  .annotate(OpenApi.Deprecated, true)
  .annotate(
    OpenApi.Description,
    'Legacy stored-TTS-id endpoint. New readers should synthesize stateless audio through POST /v2/synthesize.',
  )
  .annotate(RequestTimeout, TTS_REQUEST_TIMEOUT);

const synthesizeEndpoint = HttpApiEndpoint.post('Synthesize', '/synthesize', {
  payload: SynthesizePayload,
  success: SynthesizeResponse.pipe(HttpApiSchema.status(200)),
  error: [
    BadRequestError,
    NotFoundError,
    UnauthorizedError,
    RateLimitedError,
    InternalServerError,
  ],
})
  .middleware(OptionalUserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Synthesize stateless audio')
  .annotate(
    OpenApi.Description,
    'Synthesize audio from doc-pack page/char ranges or raw text without stored TTS chunk IDs.',
  )
  .annotate(RequestTimeout, TTS_REQUEST_TIMEOUT);

const demoTextEndpoint = HttpApiEndpoint.post('DemoText', '/tts/demo-text', {
  payload: DemoTextPayload,
  success: TtsDetailResponse.pipe(HttpApiSchema.status(200)),
  error: [BadRequestError, RateLimitedError, InternalServerError],
})
  .annotate(OpenApi.Summary, 'Demo TTS')
  .annotate(
    OpenApi.Description,
    `Unauthenticated demo TTS for homepage. ${DEMO_TEXT_MAX_LENGTH.toString()} character max.`,
  )
  .annotate(RequestTimeout, TTS_REQUEST_TIMEOUT);

export const ttsGroup = HttpApiGroup.make('Tts')
  .add(getChapterTtsEndpoint)
  .add(batchTextEndpoint)
  .add(getTtsEndpoint)
  .add(synthesizeEndpoint)
  .add(demoTextEndpoint)
  .prefix('/v2');
